import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Upload, Music, Server, CheckCircle2, XCircle, Loader2, Download, AlertCircle } from 'lucide-react';
import { parseYouTubeMusicCSV, type Track } from '@/lib/youtube-music-parser';
import { MusicseerrClient } from '@/lib/musicseerr-client';
import { AlbumMatcher, type AlbumMatch } from '@/lib/album-matcher';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type ProcessingStatus = 'idle' | 'parsing' | 'matching' | 'syncing' | 'complete';

const Index = () => {
  useSeoMeta({
    title: 'YouTube Music → Musicseerr Sync',
    description: 'Sync your YouTube Music library to Musicseerr. Export your YouTube Music data and automatically request albums in your Musicseerr instance.',
  });

  // Connection state
  const [musicseerrUrl, setMusicseerrUrl] = useState('http://localhost:8688');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  // Processing state
  const [status, setStatus] = useState<ProcessingStatus>('idle');
  const [tracks, setTracks] = useState<Track[]>([]);
  const [matches, setMatches] = useState<AlbumMatch[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentItem, setCurrentItem] = useState('');
  
  // Results
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errors, setErrors] = useState<Array<{ album: string; error: string }>>([]);

  const testConnection = async () => {
    try {
      setConnectionError('');
      const client = new MusicseerrClient(musicseerrUrl);
      await client.testConnection();
      setIsConnected(true);
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
      setIsConnected(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setStatus('parsing');
      setCurrentItem('Parsing CSV file...');
      const parsedTracks = await parseYouTubeMusicCSV(file);
      setTracks(parsedTracks);
      
      // Start matching
      setStatus('matching');
      setTotalItems(parsedTracks.length);
      setCurrentProgress(0);
      
      const matcher = new AlbumMatcher();
      const albumMatches = await matcher.matchTracksToAlbums(parsedTracks, (progress, current) => {
        setCurrentProgress(progress);
        setCurrentItem(current);
      });
      
      setMatches(albumMatches);
      setStatus('idle');
      setCurrentItem('');
    } catch (error) {
      console.error('Error processing file:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to process file');
      setStatus('idle');
    }
  };

  const startSync = async () => {
    if (!isConnected || matches.length === 0) return;

    try {
      setStatus('syncing');
      setSuccessCount(0);
      setErrorCount(0);
      setErrors([]);
      setTotalItems(matches.length);
      setCurrentProgress(0);

      const client = new MusicseerrClient(musicseerrUrl);

      for (let i = 0; i < matches.length; i++) {
        const match = matches[i];
        setCurrentItem(`${match.artist} - ${match.album}`);
        setCurrentProgress(i + 1);

        try {
          // Search for the album in Musicseerr
          const results = await client.searchAlbum(match.artist, match.album);
          
          if (results.length > 0) {
            // Request the first match
            await client.requestAlbum(results[0].id);
            setSuccessCount(prev => prev + 1);
          } else {
            setErrorCount(prev => prev + 1);
            setErrors(prev => [...prev, { 
              album: `${match.artist} - ${match.album}`, 
              error: 'Album not found in MusicBrainz' 
            }]);
          }
        } catch (error) {
          setErrorCount(prev => prev + 1);
          setErrors(prev => [...prev, { 
            album: `${match.artist} - ${match.album}`, 
            error: error instanceof Error ? error.message : 'Unknown error' 
          }]);
        }

        // Rate limiting: wait 1 second between requests
        if (i < matches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      setStatus('complete');
      setCurrentItem('');
    } catch (error) {
      console.error('Sync error:', error);
      setConnectionError(error instanceof Error ? error.message : 'Sync failed');
      setStatus('idle');
    }
  };

  const progressPercentage = totalItems > 0 ? (currentProgress / totalItems) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-950 dark:via-purple-950 dark:to-gray-950 py-12 px-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Music className="h-12 w-12 text-purple-600 dark:text-purple-400" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
              YouTube Music → Musicseerr
            </h1>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Sync your entire YouTube Music library to Musicseerr in minutes. Export, parse, and automatically request albums.
          </p>
        </div>

        {/* Connection Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Connect to Musicseerr
            </CardTitle>
            <CardDescription>
              Enter your Musicseerr instance URL. Make sure it's accessible from your browser.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="musicseerr-url">Musicseerr URL</Label>
              <div className="flex gap-2">
                <Input
                  id="musicseerr-url"
                  type="url"
                  placeholder="http://localhost:8688"
                  value={musicseerrUrl}
                  onChange={(e) => setMusicseerrUrl(e.target.value)}
                  disabled={isConnected}
                />
                <Button 
                  onClick={testConnection} 
                  disabled={isConnected || !musicseerrUrl}
                  variant={isConnected ? "default" : "outline"}
                >
                  {isConnected ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Connected
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
                {isConnected && (
                  <Button 
                    onClick={() => setIsConnected(false)} 
                    variant="ghost"
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            </div>

            {connectionError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{connectionError}</AlertDescription>
              </Alert>
            )}

            {isConnected && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>Successfully connected to Musicseerr!</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Instructions and Upload */}
        <Tabs defaultValue="instructions" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="instructions">How to Export</TabsTrigger>
            <TabsTrigger value="upload" disabled={!isConnected}>Upload & Sync</TabsTrigger>
          </TabsList>

          <TabsContent value="instructions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export Your YouTube Music Library
                </CardTitle>
                <CardDescription>
                  Follow these steps to export your YouTube Music data using Google Takeout.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ol className="space-y-4 list-decimal list-inside">
                  <li className="space-y-2">
                    <span className="font-semibold">Go to Google Takeout</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                      Visit{' '}
                      <a 
                        href="https://takeout.google.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline"
                      >
                        takeout.google.com
                      </a>
                      {' '}and sign in with your Google account.
                    </p>
                  </li>
                  <li className="space-y-2">
                    <span className="font-semibold">Deselect all products</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                      Click "Deselect all" at the top, then scroll down and check only "YouTube and YouTube Music".
                    </p>
                  </li>
                  <li className="space-y-2">
                    <span className="font-semibold">Choose YouTube Music data</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                      Click "All YouTube data included" → Deselect all → Check only "music-library-songs".
                    </p>
                  </li>
                  <li className="space-y-2">
                    <span className="font-semibold">Configure export settings</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                      Click "Next step" → Choose "Export once" → Select file type "ZIP" → Export size doesn't matter for music data.
                    </p>
                  </li>
                  <li className="space-y-2">
                    <span className="font-semibold">Download and extract</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                      Wait for Google to prepare your export (can take a few hours). Download the ZIP file and extract it. 
                      Look for the file: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">Takeout/YouTube and YouTube Music/music-library-songs/tracks.csv</code>
                    </p>
                  </li>
                  <li className="space-y-2">
                    <span className="font-semibold">Upload the CSV file</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                      Go to the "Upload & Sync" tab and upload your tracks.csv file. This tool will parse it and match albums to MusicBrainz data.
                    </p>
                  </li>
                </ol>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Note:</strong> The export process can take several hours depending on your library size. 
                    You'll receive an email when it's ready to download.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="upload">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload YouTube Music Data
                </CardTitle>
                <CardDescription>
                  Upload your tracks.csv file from Google Takeout.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* File Upload */}
                <div className="space-y-2">
                  <Label htmlFor="csv-upload">YouTube Music tracks.csv</Label>
                  <Input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    disabled={status !== 'idle' && status !== 'complete'}
                  />
                </div>

                {/* Progress */}
                {(status === 'parsing' || status === 'matching' || status === 'syncing') && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {status === 'parsing' && 'Parsing CSV file...'}
                        {status === 'matching' && 'Matching tracks to albums...'}
                        {status === 'syncing' && 'Syncing to Musicseerr...'}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {currentProgress} / {totalItems}
                      </span>
                    </div>
                    <Progress value={progressPercentage} />
                    {currentItem && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {currentItem}
                      </p>
                    )}
                  </div>
                )}

                {/* Matched Albums */}
                {matches.length > 0 && status === 'idle' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">Matched Albums</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Found {matches.length} unique albums from {tracks.length} tracks
                        </p>
                      </div>
                      <Button onClick={startSync} size="lg">
                        <Music className="mr-2 h-4 w-4" />
                        Start Sync
                      </Button>
                    </div>

                    <ScrollArea className="h-[300px] rounded-md border p-4">
                      <div className="space-y-2">
                        {matches.map((match, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-md"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{match.album}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {match.artist}
                              </p>
                            </div>
                            <Badge variant="secondary">{match.trackCount} tracks</Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Results */}
                {status === 'complete' && (
                  <div className="space-y-4">
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Sync complete! Requested {successCount} albums.
                        {errorCount > 0 && ` ${errorCount} albums failed.`}
                      </AlertDescription>
                    </Alert>

                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                            Success
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-3xl font-bold">{successCount}</p>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <XCircle className="h-5 w-5 text-red-600" />
                            Failed
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-3xl font-bold">{errorCount}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {errors.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-semibold text-red-600 dark:text-red-400">Errors</h3>
                        <ScrollArea className="h-[200px] rounded-md border p-4">
                          <div className="space-y-2">
                            {errors.map((error, index) => (
                              <div 
                                key={index}
                                className="p-3 bg-red-50 dark:bg-red-950 rounded-md"
                              >
                                <p className="font-medium text-sm">{error.album}</p>
                                <p className="text-xs text-red-600 dark:text-red-400">{error.error}</p>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}

                    <Button 
                      onClick={() => {
                        setStatus('idle');
                        setMatches([]);
                        setTracks([]);
                        setSuccessCount(0);
                        setErrorCount(0);
                        setErrors([]);
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      Start New Sync
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 pt-8 border-t">
          <p>
            Powered by{' '}
            <a 
              href="https://musicseerr.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:underline"
            >
              Musicseerr
            </a>
            {' '}• Built with{' '}
            <a 
              href="https://shakespeare.diy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:underline"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Index;
